/**
 * Pool Reports CSV Export Utilities
 *
 * Specialized functions for exporting pool reports to CSV format
 * Matches Angular's formatReportHeadingCsv pattern
 */

import { CSVService, createSpacerRow } from './csvExport';
import { formatNumber as baseFormatNumber, formatPercent } from './formatters';
import type { ReportsHeading, Report } from '@/types/poolReports.types';

/**
 * Format heading data for CSV export
 * Matches Angular pattern with concatenated offhire field
 */
function formatHeadingRow(
  heading: ReportsHeading,
  year: number,
  label: string
): (string | number)[] {
  return [
    `${year} ${label}`,
    baseFormatNumber(heading.averagePoints, 2, 2),
    baseFormatNumber(heading.averageEarnings, 0, 0),
    baseFormatNumber(heading.oneHundredPointsEarnings, 0, 0),
    `${baseFormatNumber(heading.totalOffhire, 0, 0)} / ${formatPercent(heading.percentOffhire, 2)}`, // Combined field like Angular
    baseFormatNumber(heading.averageFleet, 2, 2),
    baseFormatNumber(heading.adminOnHireDay, 0, 0),
    baseFormatNumber(heading.bunkerOnHireDay, 0, 0),
    baseFormatNumber(heading.averageVesselDistribution, 0, 0),
    baseFormatNumber(heading.oneHundredPointsDistribution, 0, 0),
    baseFormatNumber(heading.workingCapitalBFwd, 0, 0),
  ];
}

/**
 * Format detail row for CSV export
 * Matches Angular pattern: removes estimatedSpot and weightedDaysUnifixed for specific report types
 */
function formatDetailRow(item: Report, reportType: 'yearly' | 'monthly'): (string | number)[] {
  const row = [
    item.rowLabel,
    baseFormatNumber(item.vesselDays, 0, 0),
    baseFormatNumber(item.weightedDaysSpot, 0, 0),
    baseFormatNumber(item.oneHundredPointsFixedRatesSpot, 0, 0),
    baseFormatNumber(item.total, 0, 0),
    baseFormatNumber(item.contribution, 0, 0),
    baseFormatNumber(item.distribution, 0, 0),
    baseFormatNumber(item.adminAndBunker, 0, 0),
    baseFormatNumber(item.workingCapital, 0, 0),
  ];

  // For monthly reports, include additional columns
  if (reportType === 'monthly') {
    row.splice(3, 0, baseFormatNumber(item.weightedDaysUnifixed, 0, 0));
    row.splice(4, 0, baseFormatNumber(item.estimatedSpot, 0, 0));
  }

  return row;
}

/**
 * Export summary report to CSV with Angular-compatible format
 * Includes spacer rows and proper column removal
 */
export function exportSummaryToCSV(
  headings: ReportsHeading[],
  details: Report[],
  reportType: 'yearly' | 'monthly',
  reportName: string,
  year: number
) {
  const rows: (string | number)[][] = [];

  // Section 1: Heading rows
  headings.forEach((heading) => {
    const label = heading.isYTDValue ? 'YTD' : heading.isFullYearValue ? 'Full Year' : '';
    const headingRow = formatHeadingRow(heading, year, label);
    rows.push(headingRow);
  });

  // Add spacer between headings and details
  if (headings.length > 0 && details.length > 0) {
    const columnCount = reportType === 'yearly' ? 9 : 11;
    rows.push(createSpacerRow(columnCount));
  }

  // Section 2: Detail rows with spacers (Angular pattern)
  if (details.length > 0) {
    // Define which rows should have spacers before them (Angular withSpacer array)
    const withSpacer = ['1st Quarter', 'Year to Date', 'Full Year'];
    const columnCount = reportType === 'yearly' ? 9 : 11;

    details.forEach((item) => {
      // Add spacer row before specific sections
      if (withSpacer.includes(item.rowLabel)) {
        rows.push(createSpacerRow(columnCount));
      }

      const detailRow = formatDetailRow(item, reportType);
      rows.push(detailRow);

      // For quarters with months, add month rows
      if (item.months && item.months.length > 0) {
        item.months.forEach((month) => {
          const monthRow = formatDetailRow(month, reportType);
          rows.push(monthRow);
        });
      }
    });
  }

  // Define column headers based on report type
  const headingHeaders = [
    '',
    'Average Points',
    'Average Earnings',
    '100 Points Earnings',
    'Total Offhire / %',
    'Average Fleet',
    'Admin Onhire Day',
    'Bunker Onhire Day',
    'Avg Vessel Distribution',
    '100 Points Distribution',
    'Working Capital B/Fwd',
  ];

  const detailHeaders =
    reportType === 'yearly'
      ? [
          'Period',
          'Vessel Days',
          'Weighted Days (Spot)',
          '100 Pts Fixed Rates (Spot)',
          'Total',
          'Contribution',
          'Distribution',
          'Admin, Bunker & Loan',
          'Working Capital',
        ]
      : [
          'Period',
          'Vessel Days',
          'Weighted Days (Spot)',
          'Weighted Days (Unfixed)',
          '100 Pts Fixed Rates (Spot)',
          'Estimate (Spot)',
          'Total',
          'Contribution',
          'Distribution',
          'Admin, Bunker & Loan',
          'Working Capital',
        ];

  // Use the appropriate headers (we've mixed both sections, but Angular does this too)
  const headers = headings.length > 0 ? headingHeaders : detailHeaders;

  // Export with filename matching Angular pattern
  const filename = `${reportName}-summary-report`;
  CSVService.export(rows, filename, headers);
}
